import React, {useCallback, useEffect, useReducer, useRef, useState} from "react";
import '../utils/i18n';
import Map, {LoadingStatus, MapAPI, ReloadButton} from "./map";
import SearchSidebar, {SidebarAPI} from "./search_sidebar";
import {MapInfo, SearchQueryTagList} from "../models/search_query";
import "../../../assets/stylesheets/reshaped/gintoTheme/theme.css";
import {RatingProfileId, SearchAction, SearchState} from "../models/models";
import {RatingProfileSwitcher} from "./rating_profile_switcher";
import {useInIframe, useSearchState} from "../utils/routing_hooks";
import {geoLocationFromPlaceSearchQueryTag} from "../utils/helpers";
import {GeoPositionProvider} from "../utils/geo_position_provider";
import {Button} from "reshaped";
import {ChevronLeftIcon, ListIcon, MapIcon, SearchIcon} from "lucide-react";
import {useTranslation} from "react-i18next";

const SIDEBAR_WIDTH = 420;

function searchReducer(state: SearchState, action: SearchAction): SearchState {
    switch (action.type) {
        case 'MAP_INFO_CHANGED': {
            return {
                ...state,
                mapInfo: action.mapInfo
            }
        }
        case 'TAGS_CHANGED': {
            return {
                ...state,
                searchQueryTags: action.searchQueryTags
            }
        }
        case 'RATING_PROFILE_SELECTION_CHANGED': {
            return {
                ...state,
                ratingProfileId: action.ratingProfileId
            }
        }
    }
}

export function SearchMap() {
    const { t } = useTranslation();
    const [initialSearchState, setInitialSearchState] = useSearchState();
    const inIframe = useInIframe();
    const mapRef = useRef<MapAPI>(null);
    const sidebarRef = useRef<SidebarAPI>(null);
    const [searchState, dispatch] = useReducer(searchReducer, initialSearchState);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth < 900);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("loaded");

    useEffect(() => {
        setInitialSearchState(searchState);
    }, [searchState, setInitialSearchState]);

    const {ratingProfileId, searchQueryTags} = searchState;
    const placeTag = searchQueryTags.find(tag => tag.type == "place");
    const placeTagPosition = geoLocationFromPlaceSearchQueryTag(placeTag);

    const handleSelectedTagsChange = (queryTags: SearchQueryTagList) => {
        dispatch({ type: 'TAGS_CHANGED', searchQueryTags: queryTags });

        // move map to place tag if one has been added
        const prevPlaceTag = searchQueryTags.find(t => t.type == "place");
        const prevLocation = geoLocationFromPlaceSearchQueryTag(prevPlaceTag);
        const newPlaceTag = queryTags.find(t => t.type == "place");
        const newLocation = geoLocationFromPlaceSearchQueryTag(newPlaceTag);
        if (newLocation && !(newLocation.lat == prevLocation?.lat && newLocation.lng == prevLocation?.lng)) {
            const map = mapRef.current;
            if (map) {
                map.setView(newLocation);
            }
        }
    };

    const handleRatingProfileSelected = (newRatingProfileId: RatingProfileId) => {
        dispatch({ type: 'RATING_PROFILE_SELECTION_CHANGED', ratingProfileId: newRatingProfileId });
    };

    const handleMapMoved = useCallback((newMapInfo: MapInfo) => {
        dispatch({ type: 'MAP_INFO_CHANGED', mapInfo: newMapInfo });
    }, []);

    const handleCollapse = () => {
        setSidebarCollapsed(true);
    };

    const handleExpandWithSearch = () => {
        setSidebarCollapsed(false);
        setTimeout(() => sidebarRef.current?.focusSearch(), 0);
    };

    const handleExpandWithResults = () => {
        setSidebarCollapsed(false);
        setTimeout(() => sidebarRef.current?.focusFirstResult(), 500);
    };

    const handleReload = () => {
        mapRef.current?.reload();
    };

    const sidebarClassName = [
        "bg-page z-200 overflow-hidden",
        // Desktop: grid column 1, width transition
        "l:col-start-1 l:col-end-2 l:row-span-full l:h-full",
        "l:transition-[width] l:duration-300",
        sidebarCollapsed
            ? "l:w-0"
            : "l:w-[420px] l:shadow-[0_5px_10px_0px_rgba(0,0,0,0.05),0_15px_25px_0px_rgba(0,0,0,0.07)]",
        // Mobile: absolute overlay from bottom, height transition
        "max-l:absolute max-l:bottom-0 max-l:left-0 max-l:w-full",
        "max-l:transition-[height] max-l:duration-300",
        sidebarCollapsed ? "max-l:h-0" : "max-l:h-full",
        "print:relative print:w-full print:h-auto print:pt-2",
    ].join(" ");

    return (
        <GeoPositionProvider>
            {/* Grid: sidebar (auto) | controls (1fr). Map fills entire grid via absolute. */}
            <div className="relative h-full l:grid l:grid-cols-[auto_1fr] l:grid-rows-[1fr] print:flex print:flex-col">
                {/* Sidebar: column 1 on desktop (shifts map controls), bottom overlay on mobile */}
                <div className={sidebarClassName} inert={sidebarCollapsed}>
                    <SearchSidebar ref={sidebarRef} searchState={searchState} onSelectedQueryTagsChange={handleSelectedTagsChange} />
                </div>
                {/* Controls: column 2 on desktop (shifts with sidebar), full-screen on mobile.
                    pointer-events-none so map interactions pass through transparent areas. */}
                <div className="l:col-start-2 l:col-end-3 l:row-span-full relative max-l:absolute max-l:inset-0 pointer-events-none print:hidden">
                    <div className="pointer-events-auto">
                        <RatingProfileSwitcher ratingProfileId={ratingProfileId} onRatingProfileSelected={handleRatingProfileSelected} />
                    </div>
                    <div className="absolute bottom-2.5 left-2.5 z-250 flex flex-col gap-1.5 pointer-events-auto">
                        {sidebarCollapsed ? (
                            <>
                                <Button color={"media"} elevated icon={<SearchIcon />} onClick={handleExpandWithSearch} aria-label={t("map.sidebar.open_search")} attributes={{ "title": t("map.sidebar.open_search") }} />
                                <Button color={"media"} elevated icon={<ListIcon />} onClick={handleExpandWithResults} aria-label={t("map.sidebar.open_results")} attributes={{ "title": t("map.sidebar.open_results") }} />
                            </>
                        ) : (
                            <>
                                <div className="l:hidden">
                                    <Button color={"media"} elevated icon={<MapIcon />} onClick={handleCollapse} aria-label={t("map.sidebar.show_map")} attributes={{ "title": t("map.sidebar.show_map") }} />
                                </div>
                                <div className="max-l:hidden">
                                    <Button color={"media"} elevated icon={<ChevronLeftIcon />} onClick={handleCollapse} aria-label={t("map.sidebar.collapse")} attributes={{ "title": t("map.sidebar.collapse") }} />
                                </div>
                            </>
                        )}
                    </div>
                    <div className="absolute w-auto bottom-4 left-1/2 -translate-x-1/2 z-150 grid pointer-events-auto">
                        {inIframe && (
                            <div className="[grid-area:1/1]">
                                <a href={"https://www.ginto.guide"} target="_blank">
                                    <img src={RAILS_ASSET_URL("layout/ginto-logo-all-color.svg")} alt={t("map.logo")} className="h-9" />
                                </a>
                            </div>
                        )}
                        {loadingStatus !== "loaded" && (
                            <div className="[grid-area:1/1]">
                                <ReloadButton status={loadingStatus} onReload={handleReload} />
                            </div>
                        )}
                    </div>
                </div>
                {/* Map always spans full viewport, behind all grid items */}
                <div className="absolute inset-0 print:hidden z-100">
                    <Map ref={mapRef} searchState={searchState} placeTagPosition={placeTagPosition} onMapSearch={handleMapMoved} onLoadingStatusChange={setLoadingStatus} sidebarPaddingLeft={sidebarCollapsed ? 0 : SIDEBAR_WIDTH} />
                </div>
            </div>
        </GeoPositionProvider>
    );
}
